(() => {
  if (window.HobbyTigerCollectionEngine?.__hobbyTigerEngineSingleton) return;

  const batchSize = 24;
  const collectionText = window.HobbyTigerCollectionStrings || {};
  const collectionT = (key, fallback, replacements = {}) => {
    let text = String(collectionText[key] || fallback || '');
    Object.entries(replacements).forEach(([token, value]) => {
      text = text.split(`[${token}]`).join(String(value));
    });
    return text;
  };
  const labels = {
    'in-stock': collectionT('inStock', 'In stock'),
    'out-of-stock': collectionT('outOfStock', 'Out of stock'),
  };
  const filterLabels = {
    franchise: 'Franchise',
    character: 'Character',
    robot: 'Robot',
    brand: 'Brand',
    collection: 'Collection',
    series: 'Series',
    productLine: 'Product Line',
    cover: 'Cover',
    type: 'Type',
    manufacturer: 'Manufacturer',
    author: 'Author',
    publisher: 'Publisher',
    year: 'Year',
    availability: 'Availability',
    platform: 'Platform',
    edition: 'Edition',
    genre: 'Genre',
    language: 'Language',
    subtitles: 'Subtitle language',
    package: 'Package',
  };
  const profileFilters = {
    all_figures: ['franchise', 'character', 'robot', 'productLine', 'type', 'manufacturer'],
    figures: ['franchise', 'character', 'robot', 'productLine', 'type', 'manufacturer'],
    ichiban_kuji: ['franchise', 'character', 'robot', 'collection', 'series'],
    banpresto_crane_game: ['franchise', 'character', 'robot', 'series'],
    manga: ['franchise', 'author', 'productLine', 'year', 'publisher'],
    magazines: ['cover', 'productLine', 'year', 'publisher'],
    video_games: ['franchise', 'platform', 'edition', 'genre', 'year', 'language', 'manufacturer'],
    bluray: ['franchise', 'year', 'language', 'subtitles', 'publisher', 'availability'],
    books_media: ['type', 'franchise', 'author', 'publisher', 'availability'],
    books_media_publisher: ['type', 'franchise', 'author', 'year', 'publisher', 'availability'],
    pre_order: ['franchise', 'manufacturer'],
    generic: ['availability', 'type', 'franchise', 'manufacturer'],
  };
  const stateStoragePrefix = 'hobbytiger:collection-state:';
  const stateMaxAge = 30 * 60 * 1000;
  const priceCache = new Map();
  const priceQueue = [];
  const queuedPriceHandles = new Set();
  let activePriceRequests = 0;
  let priceObserverStarted = false;
  let priceQueueOrder = 0;
  let priceReprioritizeQueued = false;
  let priceScrollListenerStarted = false;

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character]));

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const selectorEscape = (value) =>
    window.CSS?.escape ? window.CSS.escape(String(value || '')) : String(value || '').replace(/["\\]/g, '\\$&');
  const slugify = (value) =>
    normalize(value)
      .replace(/&/g, ' and ')
      .replace(/\+/g, ' plus ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const storefrontLocalePrefix = (() => {
    const root = String(window.Shopify?.routes?.root || '').replace(/\/$/, '');
    if (/^\/[a-z]{2}(?:-[a-z]{2})?$/i.test(root)) return root;

    const firstPathPart = window.location.pathname.split('/').filter(Boolean)[0] || '';
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstPathPart) ? `/${firstPathPart}` : '';
  })();

  const localizeStorefrontUrl = (url, fallbackUrl = '') => {
    const value = String(url || fallbackUrl || '').trim();
    if (!value) return '#';
    if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(value)) return value;

    const normalizedUrl = value.startsWith('/') ? value : `/${value}`;
    if (!storefrontLocalePrefix || normalizedUrl.startsWith(`${storefrontLocalePrefix}/`)) return normalizedUrl;

    const canLocalize = [
      '/products/',
      '/collections/',
      '/pages/',
      '/blogs/',
      '/policies/',
      '/search',
    ].some((prefix) => normalizedUrl === prefix.replace(/\/$/, '') || normalizedUrl.startsWith(prefix));

    return canLocalize ? `${storefrontLocalePrefix}${normalizedUrl}` : normalizedUrl;
  };

  const storefrontLocale = normalize(window.Shopify?.locale || document.documentElement.lang || storefrontLocalePrefix.replace('/', '') || 'en').replace('_', '-');
  const storefrontLanguage = storefrontLocale.split('-')[0];
  const productCardStrings = window.HobbyTigerThemeStrings?.productCard || {};
  const fallbackProductCardStrings = {
    addToCart: 'Add to cart',
    favoriteAria: 'Add [title] to favorites',
    preorder: 'Pre-Order',
    preorderFinished: 'Pre-order finished',
    justReleased: 'Just released',
    used: 'Used',
    brandNew: 'Brand New',
    preorderUntil: 'Pre-order until [date]',
    saleBadge: 'Sale',
    soldOut: 'Sold out',
    thisProduct: 'this product',
    viewDetails: 'View details',
    viewDetailsAria: 'View details about [title]',
  };
  const decodeHtmlEntities = (value) => {
    const textValue = String(value || '');
    if (!textValue.includes('&')) return textValue;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = textValue;
    return textarea.value;
  };
  const text = (key) => decodeHtmlEntities(productCardStrings[key] || fallbackProductCardStrings[key] || '');
  const phrase = (key, replacements = {}) => {
    let translation = text(key);
    Object.entries(replacements).forEach(([name, value]) => {
      translation = translation.replaceAll(`[${name}]`, value || '');
    });
    return translation;
  };

  const fieldValues = (product, key) => {
    if (!product || !product.fields) return [];
    const values = product.fields[key] || [];
    return Array.isArray(values) ? values.filter(Boolean) : [];
  };

  const liveCollectionCache = new Map();

  const parsePriceCents = (value) => {
    const price = Number.parseFloat(String(value || '0').replace(/[^\d.]/g, ''));
    return Number.isFinite(price) ? Math.round(price * 100) : 0;
  };

  const formatPriceText = (priceCents) => `¥${Math.round((priceCents || 0) / 100).toLocaleString('en-US')} JPY`;

  const injectPriceSkeletonStyles = () => {
    if (document.getElementById('hobbytiger-dynamic-price-styles')) return;
    const style = document.createElement('style');
    style.id = 'hobbytiger-dynamic-price-styles';
    style.textContent = `
      .ht-price-skeleton {
        animation: ht-price-skeleton-pulse 1.1s ease-in-out infinite;
        background: linear-gradient(90deg, rgba(18,24,33,.08), rgba(18,24,33,.16), rgba(18,24,33,.08));
        background-size: 200% 100%;
        border-radius: 999px;
        display: inline-block;
        height: 1.45rem;
        width: 7.4rem;
      }

      @keyframes ht-price-skeleton-pulse {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `;
    document.head.appendChild(style);
  };

  const productPriceUrl = (productUrl, handle) => {
    const url = productUrl || (handle ? `/products/${handle}` : '');
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}view=favorite-data`;
  };

  const normalizePricePayload = (payload) => {
    if (typeof payload === 'string') {
      return { priceText: payload, compareAtPriceText: '', onSale: false, saleBadge: '' };
    }
    return {
      priceText: payload?.priceText || payload?.displayPrice || payload?.price || '',
      compareAtPriceText: payload?.compareAtPriceText || payload?.compareAtPrice || '',
      onSale: payload?.onSale === true || payload?.onSale === 'true',
      saleBadge: payload?.saleBadge || text('saleBadge'),
    };
  };

  const renderHydratedPriceMarkup = (priceData) => {
    if (!priceData.priceText) return '';
    if (!priceData.onSale || !priceData.compareAtPriceText) {
      return `
        <div class="price__regular">
          <span class="price-item price-item--regular">${escapeHtml(priceData.priceText)}</span>
        </div>
        <div class="price__sale"></div>
      `;
    }
    return `
      <div class="price__regular">
        <span class="price-item price-item--regular">${escapeHtml(priceData.priceText)}</span>
      </div>
      <div class="price__sale">
        <span class="visually-hidden">Regular price</span>
        <s class="price-item price-item--regular">${escapeHtml(priceData.compareAtPriceText)}</s>
        <span class="visually-hidden">Sale price</span>
        <span class="price-item price-item--sale price-item--last">${escapeHtml(priceData.priceText)}</span>
      </div>
    `;
  };

  const applyHydratedPrice = (handle, payload, root = document) => {
    const priceData = normalizePricePayload(payload);
    if (!handle || !priceData.priceText) return;
    root.querySelectorAll(`[data-ht-price-product-handle="${selectorEscape(handle)}"]`).forEach((node) => {
      const price = node.closest('.price');
      const container = price?.querySelector('.price__container') || node;
      if (price) price.classList.toggle('price--on-sale', priceData.onSale && !!priceData.compareAtPriceText);
      container.innerHTML = renderHydratedPriceMarkup(priceData);
      container.dataset.htPriceProductHandle = handle;
      container.dataset.htPriceProductUrl = node.dataset.htPriceProductUrl || '';
      container.dataset.htPriceHydrated = 'true';
      node.removeAttribute('aria-busy');
      node.dataset.htPriceHydrated = 'true';
    });
    root.querySelectorAll(`[data-ht-sale-badge-product-handle="${selectorEscape(handle)}"]`).forEach((node) => {
      if (priceData.onSale && priceData.saleBadge) {
        node.textContent = priceData.saleBadge;
        node.hidden = false;
      } else {
        node.hidden = true;
      }
    });
    root.querySelectorAll(`[data-ht-dataset-condition][data-ht-condition-handle="${selectorEscape(handle)}"]`).forEach((node) => {
      node.hidden = priceData.onSale;
    });
    root.querySelectorAll(`[data-hobbytiger-favorite-button][data-product-handle="${selectorEscape(handle)}"]`).forEach((button) => {
      button.dataset.productPrice = priceData.priceText;
    });
  };

  const fetchText = async (url, accept = 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8', timeoutMs = 10000) => {
    if (typeof fetch === 'function') {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timeout = window.setTimeout(() => controller?.abort(), timeoutMs);
      try {
        const fetchPromise = fetch(url, {
          headers: { Accept: accept },
          credentials: 'same-origin',
          signal: controller?.signal,
        });
        const timeoutPromise = new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('Timed out loading product price.')), timeoutMs);
        });
        const response = await Promise.race([fetchPromise, timeoutPromise]);
        if (!response.ok) throw new Error(`Could not load product price: ${response.status}`);
        return response.text();
      } finally {
        window.clearTimeout(timeout);
      }
    }

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('GET', url, true);
      request.withCredentials = true;
      request.timeout = timeoutMs;
      request.setRequestHeader('Accept', accept);
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          resolve(request.responseText || '');
        } else {
          reject(new Error(`Could not load product price: ${request.status}`));
        }
      };
      request.ontimeout = () => reject(new Error('Timed out loading product price.'));
      request.onerror = () => reject(new Error('Could not load product price.'));
      request.send();
    });
  };

  const priceFromProductHtml = (html) => {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const mainProductPrice = doc.querySelector('.ht-product__price');
    const mainProductCompare = doc.querySelector('.ht-product__compare');
    const priceText = (
      mainProductPrice?.textContent ||
      doc.querySelector('.price .price-item--regular')?.textContent ||
      ''
    ).trim();
    const compareAtPriceText = (
      mainProductPrice
        ? mainProductCompare?.textContent || ''
        : doc.querySelector('.price s.price-item--regular')?.textContent ||
          ''
    ).trim();
    const saleBadge = mainProductPrice
      ? ''
      : (
          doc.querySelector('.card__badge .ht-card-sale-badge')?.textContent ||
          ''
        ).trim();
    if (!priceText) return null;
    return {
      priceText,
      compareAtPriceText,
      onSale: !!compareAtPriceText && compareAtPriceText !== priceText,
      saleBadge: saleBadge || text('saleBadge'),
    };
  };

  const fetchProductPrice = async ({ handle, url }) => {
    try {
      const endpointText = await fetchText(productPriceUrl(url, handle), 'application/json', 1400);
      let payload = null;
      payload = JSON.parse(endpointText);
      const jsonPrice = typeof payload?.displayPrice === 'string' ? payload.displayPrice : typeof payload?.price === 'string' ? payload.price : '';
      if (jsonPrice) {
        return {
          priceText: jsonPrice,
          compareAtPriceText: typeof payload?.compareAtPrice === 'string' ? payload.compareAtPrice : '',
          onSale: payload?.onSale === true,
          saleBadge: typeof payload?.saleBadge === 'string' ? payload.saleBadge : text('saleBadge'),
        };
      }
    } catch (error) {
      // Fall through to the product page. Some Shopify contexts do not serve alternate views consistently.
    }

    const productUrl = url || (handle ? `/products/${handle}` : '');
    if (productUrl) {
      const productPagePrice = priceFromProductHtml(await fetchText(productUrl));
      if (productPagePrice) return productPagePrice;
    }
    return '';
  };

  const priceNodeForHandle = (handle) => {
    if (!handle) return null;
    return document.querySelector(`[data-ht-price-product-handle="${selectorEscape(handle)}"]:not([data-ht-price-hydrated="true"])`);
  };

  const pricePriorityScore = (item) => {
    const node = priceNodeForHandle(item.handle);
    if (!node || typeof node.getBoundingClientRect !== 'function') return Number.MAX_SAFE_INTEGER;
    const rect = node.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const priorityMargin = Math.max(360, Math.round(viewportHeight * 1.15));
    if (rect.bottom >= -priorityMargin && rect.top <= viewportHeight + priorityMargin) {
      const viewportCenter = viewportHeight / 2;
      const nodeCenter = rect.top + rect.height / 2;
      return Math.abs(nodeCenter - viewportCenter);
    }
    if (rect.top > viewportHeight) return rect.top - viewportHeight + priorityMargin;
    return Math.abs(rect.bottom) + priorityMargin;
  };

  const sortPriceQueue = () => {
    priceQueue.sort((a, b) => {
      const scoreDelta = pricePriorityScore(a) - pricePriorityScore(b);
      if (scoreDelta !== 0) return scoreDelta;
      return (a.order || 0) - (b.order || 0);
    });
  };

  const processPriceQueue = () => {
    sortPriceQueue();
    while (activePriceRequests < 6 && priceQueue.length) {
      const item = priceQueue.shift();
      activePriceRequests += 1;
      fetchProductPrice(item)
        .then((priceData) => {
          if (!priceData) return;
          priceCache.set(item.handle, priceData);
          applyHydratedPrice(item.handle, priceData);
        })
        .catch((error) => {
          console.warn('[HobbyTiger] Could not hydrate product price.', error);
        })
        .finally(() => {
          activePriceRequests -= 1;
          queuedPriceHandles.delete(item.handle);
          processPriceQueue();
        });
    }
  };

  const hydratePrices = (root = document) => {
    injectPriceSkeletonStyles();
    root.querySelectorAll('[data-ht-price-product-handle]:not([data-ht-price-hydrated="true"])').forEach((node) => {
      const handle = node.dataset.htPriceProductHandle || '';
      if (!handle) return;
      const cachedPrice = priceCache.get(handle);
      if (cachedPrice) {
        applyHydratedPrice(handle, cachedPrice);
        return;
      }
      if (queuedPriceHandles.has(handle)) return;
      queuedPriceHandles.add(handle);
      priceQueue.push({ handle, url: node.dataset.htPriceProductUrl || '', order: ++priceQueueOrder });
    });
    processPriceQueue();
  };

  const reprioritizePriceQueue = () => {
    if (!priceQueue.length) return;
    sortPriceQueue();
    processPriceQueue();
  };

  const schedulePriceReprioritize = () => {
    if (priceReprioritizeQueued) return;
    priceReprioritizeQueued = true;
    window.requestAnimationFrame(() => {
      priceReprioritizeQueued = false;
      hydratePrices(document);
      reprioritizePriceQueue();
    });
  };

  const startPriceScrollPriority = () => {
    if (priceScrollListenerStarted) return;
    priceScrollListenerStarted = true;
    window.addEventListener('scroll', schedulePriceReprioritize, { passive: true });
    window.addEventListener('resize', schedulePriceReprioritize, { passive: true });
    window.addEventListener('pageshow', schedulePriceReprioritize);
  };

  const startPriceObserver = () => {
    if (priceObserverStarted || !('MutationObserver' in window)) return;
    priceObserverStarted = true;
    const observer = new MutationObserver((mutations) => {
      let shouldHydrate = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1 || shouldHydrate) return;
          shouldHydrate = node.matches?.('[data-ht-price-product-handle]') || !!node.querySelector?.('[data-ht-price-product-handle]');
        });
      });
      if (shouldHydrate) window.requestAnimationFrame(() => {
        hydratePrices(document);
        schedulePriceReprioritize();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  const liveCollectionHandle = (section) => {
    const handle = section?.dataset?.htCollectionHandle || '';
    if (!handle || handle === 'all') return '';
    return handle;
  };

  const liveProductsUrl = (section, page) => {
    const handle = liveCollectionHandle(section);
    if (section?.dataset?.htFilterProfile === 'bluray') {
      return `/collections/${encodeURIComponent(handle || 'blu-ray')}?view=hobbytiger-bluray-data&page=${page}`;
    }
    const base = handle ? `/collections/${encodeURIComponent(handle)}/products.json` : '/products.json';
    return `${base}?limit=250&page=${page}`;
  };

  const getPrimaryVariant = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants[0] || {};
  };

  const blurayLanguages = (value) => [...new Set(String(value || '')
    .split(/\s*[;,]\s*/)
    .map((language) => language.replace(/\s+subtitles?$/i, '').trim())
    .filter((language) => language && !/^(none|unknown|not specified|n\/a)$/i.test(language)))];

  const liveProductToDatasetProduct = (product, section) => {
    const variant = getPrimaryVariant(product);
    const image = product?.featured_image || product?.image?.src || product?.images?.[0]?.src || '';
    const price = parsePriceCents(variant.price);
    const createdAt = product?.created_at || '';
    const createdTs = createdAt ? Math.floor(new Date(createdAt).getTime() / 1000) : 0;
    const productType = product?.product_type || '';
    const vendor = product?.vendor || '';
    const handle = product?.handle || '';
    const collectionHandle = section?.dataset?.htCollectionHandle || '';
    const isPreorderCollection = collectionHandle === 'pre-order' || collectionHandle === 'pre-order-figures';
    const media = product?.ht_metadata || {};

    return {
      id: product?.admin_graphql_api_id || product?.id || handle,
      legacyId: product?.id ? String(product.id) : handle,
      variantId: variant.admin_graphql_api_id || variant.id || '',
      variantAvailable: variant.available !== false,
      handle,
      title: product?.title || '',
      vendor,
      productType,
      createdAt,
      createdTs,
      url: handle ? `/products/${handle}` : '#',
      image,
      imageAlt: product?.title || '',
      price,
      priceText: formatPriceText(price),
      available: variant.available !== false,
      inventory: 0,
      condition: media.condition || (isPreorderCollection ? 'Pre-order' : ''),
      releaseDate: media.releaseDate || '',
      preorderDeadline: null,
      preorderUntilReleaseDate: media.preorderUntilReleaseDate || null,
      purchaseLimit: null,
      collections: collectionHandle ? [collectionHandle] : [],
      fields: {
        availability: variant.available !== false ? ['in-stock'] : ['out-of-stock'],
        franchise: media.franchise ? [media.franchise] : [],
        character: [],
        productLine: [],
        brand: [],
        collection: [],
        series: [],
        type: productType ? [productType] : [],
        manufacturer: vendor ? [vendor] : [],
        author: [],
        publisher: media.publisher ? [media.publisher] : vendor ? [vendor] : [],
        year: media.year ? [String(media.year)] : [],
        platform: [],
        edition: [],
        genre: [],
        language: blurayLanguages(media.audioLanguages),
        subtitles: blurayLanguages(media.subtitleLanguages),
        package: [],
      },
    };
  };

  const fetchLiveCollectionProducts = async (section) => {
    const handle = liveCollectionHandle(section) || 'all';
    const taxonomy = section?.dataset?.htTaxonomy || new URLSearchParams(window.location.search).get('taxonomy') || '';
    const cacheKey = `${handle}:${taxonomy}`;
    if (liveCollectionCache.has(cacheKey)) return liveCollectionCache.get(cacheKey);

    const fetchPromise = (async () => {
      const products = [];
      const isBluray = section?.dataset?.htFilterProfile === 'bluray';
      const maxPages = isBluray ? 100 : 20;
      for (let page = 1; page <= maxPages; page += 1) {
        const response = await fetch(liveProductsUrl(section, page), {
          headers: { Accept: isBluray ? 'text/html' : 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Could not load live collection products: ${response.status}`);
        const payload = await response.json();
        const pageProducts = Array.isArray(payload?.products) ? payload.products : [];
        if (isBluray && (typeof payload.hasNextPage !== 'boolean' || pageProducts.some((product) => !product.ht_metadata))) {
          throw new Error('Blu-ray collection view did not return language metadata');
        }
        products.push(...pageProducts);
        if (pageProducts.length < 250 || (isBluray && payload.hasNextPage === false)) break;
        if (isBluray && page === maxPages) throw new Error('Blu-ray pagination limit reached; refusing incomplete language facets');
      }
      return products;
    })();

    liveCollectionCache.set(cacheKey, fetchPromise);
    return fetchPromise;
  };

  const mergeWithLiveProducts = async (section, staticProducts) => {
    try {
      const liveProducts = await fetchLiveCollectionProducts(section);
      const liveByHandle = new Map(liveProducts.map((product) => [product.handle, product]));
      const richByHandle = new Map(staticProducts.map((product) => [product.handle, product]));
      const mergedProducts = staticProducts
        .filter((product) => liveByHandle.has(product.handle))
        .map((richProduct) => {
          const liveProduct = liveByHandle.get(richProduct.handle);
          const liveVariant = getPrimaryVariant(liveProduct);
          const livePrice = parsePriceCents(liveVariant.price);
          return {
            ...richProduct,
            ...(liveProduct.ht_metadata ? liveProductToDatasetProduct(liveProduct, section) : {}),
            variantId: richProduct.variantId || liveVariant.admin_graphql_api_id || liveVariant.id || '',
            variantAvailable: liveVariant.available !== false,
            available: liveVariant.available !== false,
            price: livePrice || richProduct.price,
            priceText: livePrice ? formatPriceText(livePrice) : richProduct.priceText,
            image: richProduct.image || liveProduct.featured_image || liveProduct.image?.src || liveProduct.images?.[0]?.src || '',
          };
        })
        .filter((product) => product && product.handle);

      liveProducts.forEach((liveProduct) => {
        if (!richByHandle.has(liveProduct.handle)) {
          mergedProducts.push(liveProductToDatasetProduct(liveProduct, section));
        }
      });

      return mergedProducts;
    } catch (error) {
      console.warn('[HobbyTiger] Falling back to generated collection data.', error);
      return staticProducts;
    }
  };

  const productHasSlug = (product, slug) => {
    if (!slug) return false;
    return Object.values(product.fields || {}).some((values) =>
      (Array.isArray(values) ? values : []).some((value) => slugify(value) === slug)
    );
  };

  const productMatchesCollection = (product, section) => {
    if (section.dataset.htForceEmptyAllTag === 'true') return false;

    const taxonomy = slugify(section.dataset.htTaxonomy || new URLSearchParams(window.location.search).get('taxonomy') || '');
    if (taxonomy) return productHasSlug(product, taxonomy);

    const handle = section.dataset.htCollectionHandle || '';
    const collectionHandles = product.collections || [];
    if (!handle || handle === 'all') return true;
    if (collectionHandles.includes(handle)) return true;

    if (handle === 'pre-order' || handle === 'pre-order-figures') {
      return normalize(product.condition) === 'pre-order';
    }
    if (handle === 'all-figures') {
      return normalize(product.productType) === 'figure' || collectionHandles.includes('all-figures');
    }
    if (handle === 'video-games') {
      return normalize(product.productType) === 'video game' || collectionHandles.includes('video-games');
    }
    if (handle === 'books-media') {
      const type = normalize(product.productType);
      return collectionHandles.includes('books-media') || ['manga', 'book', 'books', 'magazine'].includes(type);
    }

    return productHasSlug(product, handle);
  };

  const staticFacetCountsForSection = (dataset, section) => {
    const counts = dataset?.facetCounts?.collections;
    if (!counts || typeof counts !== 'object') return null;

    const handle = section?.dataset?.htCollectionHandle || 'all';
    return counts[handle] || (handle === 'all' ? counts.all : null) || null;
  };

  const staticFacetCount = (facetCounts, key, value) => {
    const values = facetCounts?.[key];
    if (!values || typeof values !== 'object') return null;
    return values[value] ?? values[normalize(value)] ?? null;
  };

  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const today = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const formatDate = (value) => {
    const date = parseDate(value);
    if (!date) return '';
    return date.toLocaleDateString(storefrontLocale || 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const productBadges = (product) => {
    const now = parseDate(new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10));
    const isPreorder = normalize(product.condition) === 'pre-order';
    const releaseDate = parseDate(product.releaseDate);
    const explicitDeadline = parseDate(product.preorderDeadline);
    const usesReleaseDate = product.preorderUntilReleaseDate === true || normalize(product.preorderUntilReleaseDate) === 'true';
    const effectiveDeadline = explicitDeadline || (isPreorder && usesReleaseDate ? releaseDate : null);
    const releaseArrived = releaseDate && releaseDate <= now;
    const preorderEnded = effectiveDeadline && effectiveDeadline < now;
    const showDeadlinePill = isPreorder && explicitDeadline && explicitDeadline >= now && !usesReleaseDate;
    const showFinished = isPreorder && preorderEnded && !releaseArrived;
    const inventory = Number(product.inventory);
    const showPreorder = isPreorder && !releaseArrived && !showFinished;
    const showSoldOut = product.available === false && Number.isFinite(inventory) && inventory <= 0 && !(isPreorder && !releaseArrived);
    const releaseStart = /^\d{4}-\d{2}-\d{2}$/.test(product.releaseDate || '')
      ? Date.parse(`${product.releaseDate}T00:00:00+09:00`)
      : NaN;
    const releaseExpiresAt = releaseStart + 10 * 24 * 60 * 60 * 1000;
    const validReleaseDate = Number.isFinite(releaseStart)
      && new Date(releaseStart + 9 * 60 * 60 * 1000).toISOString().slice(0, 10) === product.releaseDate;
    const showJustReleased = product.available === true && !showFinished && !showPreorder && !showSoldOut
      && validReleaseDate && Date.now() >= releaseStart && Date.now() < releaseExpiresAt;
    const condition = normalize(product.condition);
    const conditionBadge = !showFinished && !showPreorder && !showSoldOut && !showJustReleased
      ? condition === 'used' ? 'used' : condition === 'brand new' ? 'brand-new' : ''
      : '';

    return {
      preorderPill: showDeadlinePill ? phrase('preorderUntil', { date: formatDate(product.preorderDeadline) }) : '',
      standardBadge: showFinished ? text('preorderFinished') : showPreorder ? text('preorder') : showSoldOut ? text('soldOut') : '',
      standardBadgeClass: showPreorder ? 'ht-card-preorder-badge' : '',
      releaseExpiresAt: showJustReleased ? releaseExpiresAt : null,
      conditionBadge,
    };
  };

  const productDataAttributes = (product) => {
    const data = {
      availability: product.available ? 'in-stock' : 'out-of-stock',
      franchise: fieldValues(product, 'franchise').join('; '),
      character: fieldValues(product, 'character').join('; '),
      brand: fieldValues(product, 'brand').join('; '),
      type: fieldValues(product, 'type').join('; '),
      manufacturer: fieldValues(product, 'manufacturer').join('; '),
      author: fieldValues(product, 'author').join('; '),
      publisher: fieldValues(product, 'publisher').join('; '),
      year: fieldValues(product, 'year').join('; '),
      platform: fieldValues(product, 'platform').join('; '),
      edition: fieldValues(product, 'edition').join('; '),
      genre: fieldValues(product, 'genre').join('; '),
      language: fieldValues(product, 'language').join('; '),
      package: fieldValues(product, 'package').join('; '),
      price: product.price || 0,
      created: product.createdTs || 0,
    };

    return Object.entries(data)
      .map(([key, value]) => `data-${key}="${escapeHtml(value)}"`)
      .join(' ');
  };

  const renderFavoriteButton = (product, preorderPill) => `
    <button
      type="button"
      class="ht-favorite-button"
      aria-label="${escapeHtml(phrase('favoriteAria', { title: product.title || text('thisProduct') }))}"
      aria-pressed="false"
      data-hobbytiger-favorite-button
      data-product-handle="${escapeHtml(product.handle)}"
      data-product-title="${escapeHtml(product.title)}"
      data-product-url="${escapeHtml(localizeStorefrontUrl(product.url, product.handle ? `/products/${product.handle}` : ''))}"
      data-product-image="${escapeHtml(product.image || '')}"
      data-product-price=""
      data-product-preorder-pill="${escapeHtml(preorderPill)}"
    >
      <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
    </button>
  `;

  const renderLoadingSpinner = () => `
    <div class="loading__spinner hidden">
      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>
  `;

  const renderQuickAdd = (product, sectionId, badges) => {
    if (!product.variantId) return '';

    const productId = String(product.legacyId || product.id || product.handle || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const cartVariantId = String(product.variantId).split('/').pop();
    const formId = `quick-add-${sectionId}-${productId}`;
    const hasSellableVariant = product.variantAvailable !== false;
    const canQuickAdd = product.available && hasSellableVariant && !badges.standardBadge;
    const disabled = canQuickAdd ? '' : ' disabled';
    const variantDisabled = canQuickAdd ? '' : ' disabled';
    const label = canQuickAdd ? text('addToCart') : badges.standardBadge || text('soldOut');
    const purchaseLimit = Number.parseInt(product.purchaseLimit, 10);
    const limitAttributes = Number.isFinite(purchaseLimit) && purchaseLimit > 0
      ? `
              data-ht-quick-add-limit="true"
              data-ht-quick-add-variant="${escapeHtml(cartVariantId)}"
              data-ht-quick-add-purchase-limit="${purchaseLimit}"
              data-ht-quick-add-default-label="${escapeHtml(label)}"
              data-ht-quick-add-base-disabled="${canQuickAdd ? 'false' : 'true'}"`
      : '';

    const productUrl = escapeHtml(localizeStorefrontUrl(product.url, `/products/${product.handle || ''}`));

    /*
      Original quick-add cart/form markup preserved for easy restore.
      return `
        <div class="quick-add no-js-hidden">
          <product-form data-section-id="${escapeHtml(sectionId)}">
            <form method="post" action="/cart/add" id="${escapeHtml(formId)}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${escapeHtml(cartVariantId)}" class="product-variant-id"${variantDisabled}>
              <button
                id="${escapeHtml(formId)}-submit"
                type="submit"
                name="add"
                class="quick-add__submit button button--full-width button--secondary"
                aria-labelledby="${escapeHtml(formId)}-submit title-${escapeHtml(sectionId)}-${escapeHtml(productId)}"
                aria-live="polite"
                data-sold-out-message="true"${limitAttributes}${disabled}
              >
                <span>${label}</span>
                <span class="sold-out-message hidden">${text('soldOut')}</span>
                ${renderLoadingSpinner()}
              </button>
            </form>
          </product-form>
        </div>
      `;
    */

    return `
      <div class="quick-add no-js-hidden ht-card-view-more-wrap">
        <a
          href="${productUrl}"
          class="ht-card-view-more button button--full-width"
          aria-label="${escapeHtml(phrase('viewDetailsAria', { title: product.title || text('thisProduct') }))}"
        >
          ${text('viewDetails')}
        </a>
      </div>
    `;
  };

  const renderProductCard = (product, index, sectionId) => {
    injectPriceSkeletonStyles();
    const badges = productBadges(product);
    const image = product.image || '';
    const title = escapeHtml(product.title);
    const url = escapeHtml(localizeStorefrontUrl(product.url, `/products/${product.handle || ''}`));
    const handle = escapeHtml(product.handle || '');
    const imageAlt = escapeHtml(product.imageAlt || product.title);
    const animationOrder = (index % batchSize) + 1;
    const figureCardClass = normalize(product.productType) === 'figure' ? ' card--ht-figure-media' : '';

    return `
      <li
        id="Slide-${escapeHtml(sectionId)}-${index + 1}"
        class="ht-product-card ht-product-card--dynamic grid__item scroll-trigger animate--slide-in"
        data-ht-product-card
        ${productDataAttributes(product)}
        data-index="${index}"
        data-cascade
        style="--animation-order: ${animationOrder};"
      >
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard card--media card--extend-height${figureCardClass}" style="--ratio-percent: 125%;">
            <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: 125%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${image ? `<img src="${escapeHtml(image)}" alt="${imageAlt}" class="motion-reduce" loading="lazy" width="720" height="900">` : ''}
                </div>
              </div>
              ${badges.preorderPill ? `<span class="ht-card-preorder-pill">${escapeHtml(badges.preorderPill)}</span>` : ''}
              ${renderFavoriteButton(product, badges.preorderPill)}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${url}" class="full-unstyled-link">${title}</a>
                  </h3>
                </div>
                <div class="card__badge top right">
                  ${badges.standardBadge ? `<span class="badge badge--bottom-left ${escapeHtml(badges.standardBadgeClass || 'color-scheme-3')}">${escapeHtml(badges.standardBadge)}</span>` : ''}
                  ${badges.releaseExpiresAt ? `<span class="badge badge--bottom-left ht-card-release-badge" data-ht-release-expires-at="${badges.releaseExpiresAt}">${escapeHtml(text('justReleased'))}</span>` : ''}
                  ${!badges.standardBadge ? `<span
                    class="badge badge--bottom-left ht-card-sale-badge color-scheme-4"
                    data-ht-sale-badge-product-handle="${handle}"
                    hidden
                  ></span>` : ''}
                  ${badges.conditionBadge ? `<span class="badge badge--bottom-left ht-card-condition-badge--${badges.conditionBadge}" data-ht-dataset-condition="${badges.conditionBadge}" data-ht-condition-handle="${handle}">${escapeHtml(text(badges.conditionBadge === 'used' ? 'used' : 'brandNew'))}</span>` : ''}
                </div>
              </div>
            </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5" id="title-${escapeHtml(sectionId)}-${escapeHtml(product.legacyId || product.handle || index)}">
                    <a href="${url}" class="full-unstyled-link">${title}</a>
                  </h3>
                  <div class="card-information">
                  <div class="price">
                    <div
                      class="price__container"
                      data-ht-price-product-handle="${handle}"
                      data-ht-price-product-url="${url}"
                      aria-busy="true"
                    >
                      <div class="price__regular">
                        <span class="ht-price-hydration-target">
                          <span class="ht-price-skeleton" aria-hidden="true"></span>
                        </span>
                      </div>
                      <div class="price__sale"></div>
                    </div>
                  </div>
                </div>
              </div>
              ${renderQuickAdd(product, sectionId, badges)}
            </div>
          </div>
        </div>
      </li>
    `;
  };

  const filterMatches = (product, key, activeValues) => {
    if (!activeValues || activeValues.length === 0) return true;
    const values = fieldValues(product, key).map(normalize);
    return activeValues.some((value) => values.includes(normalize(value)));
  };

  const priceMatches = (product, priceMin, priceMax) => {
    const min = priceMin?.value ? Number(priceMin.value) * 100 : null;
    const max = priceMax?.value ? Number(priceMax.value) * 100 : null;
    if (Number.isFinite(min) && product.price < min) return false;
    if (Number.isFinite(max) && product.price > max) return false;
    return true;
  };

  const getFilterKeys = (section) => {
    if (section.dataset.htPreOrderLayout === 'true') return profileFilters.pre_order;
    const profile = section.dataset.htFilterProfile || 'figures';
    return profileFilters[profile] || profileFilters.generic;
  };

  const inferProfile = (section) => {
    if (section.dataset.htPreOrderLayout === 'true') return 'pre_order';
    return section.dataset.htFilterProfile || 'figures';
  };

  const productMatchesActive = (product, active, priceMin, priceMax, exceptKey = null) =>
    Object.entries(active).every(([key, values]) => key === exceptKey || filterMatches(product, key, values)) &&
    priceMatches(product, priceMin, priceMax);

  const applyInitialUrlFilters = (section, filterKeys, valuesByKey, products = []) => {
    const params = new URLSearchParams(window.location.search);
    const active = Object.fromEntries(filterKeys.map((key) => [key, []]));
    const aliases = {
      franchise: ['franchise'],
      character: ['character'],
      brand: ['brand'],
      collection: ['collection'],
      series: ['series'],
      productLine: ['productLine', 'product_line', 'product-line'],
      cover: ['cover'],
      type: ['type', 'product_type', 'product-type'],
      manufacturer: ['manufacturer'],
      author: ['author'],
      publisher: ['publisher'],
      year: ['year'],
      availability: ['availability'],
      platform: ['platform'],
      edition: ['edition'],
      genre: ['genre'],
      language: ['language', 'language_support', 'language-support', 'supported_language', 'supported-language', 'supported_languages', 'supported-languages'],
      subtitles: ['subtitles', 'subtitle_language', 'subtitle-language'],
      package: ['package', 'package_version', 'package-version'],
    };

    filterKeys.forEach((key) => {
      const requested = (aliases[key] || [key])
        .flatMap((alias) => params.getAll(alias))
        .flatMap((value) => value.split(';'))
        .map((value) => value.trim())
        .filter(Boolean);
      if (requested.length) active[key] = requested;
    });

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const collectionIndex = pathParts.indexOf('collections');
    const pathHandle = collectionIndex > -1 ? pathParts[collectionIndex + 1] : '';
    const pathTag = collectionIndex > -1 ? pathParts[collectionIndex + 2] : '';
    const taxonomy = params.get('taxonomy');
    const tag = slugify(pathTag || taxonomy || (taxonomy ? pathHandle : ''));
    if (tag) {
      for (const key of filterKeys) {
        if (active[key].length) continue;
        let match = Array.from(valuesByKey[key] || []).find((value) => slugify(value) === tag);
        if (!match && products.length) {
          for (const product of products) {
            match = fieldValues(product, key).find((value) => slugify(value) === tag);
            if (match) break;
          }
        }
        if (match) {
          active[key] = [match];
          break;
        }
      }
    }

    return active;
  };

  const sortProducts = (products, sortValue) => {
    const sorted = [...products];
    const releaseTimestamp = (product) => {
      const date = parseDate(product.releaseDate);
      return date ? date.getTime() : 0;
    };
    switch (sortValue) {
      case 'newest':
        return sorted.sort((a, b) => (b.createdTs || 0) - (a.createdTs || 0));
      case 'release-date':
        return sorted.sort((a, b) => {
          const releaseDiff = releaseTimestamp(b) - releaseTimestamp(a);
          return releaseDiff || (b.createdTs || 0) - (a.createdTs || 0);
        });
      case 'price-ascending':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-descending':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      default:
        return sorted;
    }
  };

  const collectionStatePath = () => `${window.location.pathname}${window.location.search}`;
  const lastCollectionStateKey = `${stateStoragePrefix}last`;

  const collectionStateKey = (section) => {
    const id = section?.id || section?.dataset?.sectionId || 'collection';
    return `${stateStoragePrefix}${collectionStatePath()}:${id}`;
  };

  const collectionRouteStateKey = () => `${stateStoragePrefix}${collectionStatePath()}`;

  const normalizeCollectionState = (state) => {
    if (!state || Date.now() - Number(state.savedAt || 0) > stateMaxAge) return null;
    if (state.path && state.path !== collectionStatePath()) return null;
    return state;
  };

  const readStoredCollectionState = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return normalizeCollectionState(JSON.parse(raw));
    } catch (error) {
      return null;
    }
  };

  const readHistoryCollectionState = () => {
    try {
      return normalizeCollectionState(window.history?.state?.hobbyTigerCollection);
    } catch (error) {
      return null;
    }
  };

  const readCollectionState = (section) =>
    readHistoryCollectionState()
    || readStoredCollectionState(collectionStateKey(section))
    || readStoredCollectionState(collectionRouteStateKey())
    || readStoredCollectionState(lastCollectionStateKey);

  const isBackForwardNavigation = (event = null) => {
    if (event?.persisted) return true;
    try {
      return performance.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
    } catch (error) {
      return false;
    }
  };

  const writeCollectionState = (section, state) => {
    const nextState = {
      ...state,
      path: collectionStatePath(),
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(collectionStateKey(section), JSON.stringify(nextState));
      sessionStorage.setItem(collectionRouteStateKey(), JSON.stringify(nextState));
      sessionStorage.setItem(lastCollectionStateKey, JSON.stringify(nextState));
    } catch (error) {
      /* sessionStorage can be unavailable in private modes. */
    }

    try {
      if (window.history?.replaceState) {
        const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
        window.history.replaceState({
          ...currentState,
          hobbyTigerCollection: nextState,
        }, '', window.location.href);
      }
    } catch (error) {
      /* noop */
    }
  };

  const clearCollectionState = (section) => {
    try {
      sessionStorage.removeItem(collectionStateKey(section));
      sessionStorage.removeItem(collectionRouteStateKey());
    } catch (error) {
      /* noop */
    }

    try {
      if (window.history?.replaceState && window.history.state?.hobbyTigerCollection) {
        const nextState = { ...window.history.state };
        delete nextState.hobbyTigerCollection;
        window.history.replaceState(nextState, '', window.location.href);
      }
    } catch (error) {
      /* noop */
    }
  };

  const initPreorder = (section, scopedProducts) => {
    const main = section.querySelector('.ht-collection__main');
    const existingGroups = Array.from(section.querySelectorAll('.ht-preorder-group'));
    const empty = section.querySelector('[data-ht-preorder-empty]');
    const clear = section.querySelector('[data-ht-preorder-clear]');
    const menus = Array.from(section.querySelectorAll('[data-ht-preorder-menu]')).map((menu) => ({
      element: menu,
      key: menu.dataset.htPreorderMenu,
      button: menu.querySelector('[data-ht-preorder-menu-button]'),
      list: menu.querySelector('[data-ht-preorder-menu-list]'),
      value: '',
    }));
    const renderRoot = document.createElement('div');
    renderRoot.setAttribute('data-ht-dynamic-preorder-root', '');
    existingGroups.forEach((group) => group.remove());
    if (empty) main.insertBefore(renderRoot, empty);

    const daysPerPage = section.dataset.htCollectionHandle === 'pre-order'
      ? Number(section.dataset.htPreorderDaysPerPage || 0)
      : 0;
    const paginationEnabled = Number.isInteger(daysPerPage) && daysPerPage > 0;
    const pageNumber = (value) => /^\d+$/.test(String(value || '')) ? Math.max(1, Number(value)) : 1;
    const pageFromUrl = () => pageNumber(new URL(window.location.href).searchParams.get('preorder_page'));
    const pagers = paginationEnabled ? ['afterend'].map((position) => {
      const nav = document.createElement('nav');
      nav.className = 'ht-preorder-pagination';
      nav.setAttribute('aria-label', collectionT('paginationLabel', 'Pagination'));
      nav.tabIndex = -1;
      renderRoot.insertAdjacentElement(position, nav);
      return nav;
    }) : [];

    const shouldUseSavedPosition = isBackForwardNavigation();
    const restoreState = shouldUseSavedPosition
      ? (readHistoryCollectionState() || readStoredCollectionState(collectionStateKey(section)) || readStoredCollectionState(collectionRouteStateKey()))
      : null;
    const active = Object.fromEntries(menus.map((menu) => [menu.key, []]));
    let restoreScrollY = restoreState ? Number(restoreState.scrollY || 0) : 0;
    let shouldRestoreScroll = restoreScrollY > 0;
    let saveScrollQueued = false;
    let initialRestoreApplied = false;
    let currentPage = paginationEnabled ? pageNumber(restoreState?.preorderPage || pageFromUrl()) : 1;
    let totalPages = 1;
    let renderedPage = 0;
    let restoreFrame;
    let restoreTimers = [];

    if (!shouldUseSavedPosition) {
      clearCollectionState(section);
    }

    if (restoreState?.active && typeof restoreState.active === 'object') {
      menus.forEach((menu) => {
        const values = Array.isArray(restoreState.active[menu.key]) ? restoreState.active[menu.key] : [];
        active[menu.key] = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).slice(0, 1);
        if (active[menu.key][0]) menu.button.textContent = active[menu.key][0];
      });
    }

    const allLabel = (key) => (
      key === 'manufacturer'
        ? collectionT('allManufacturers', 'All manufacturers')
        : collectionT('allFranchises', 'All franchises')
    );
    const matches = (product, exceptKey = null) =>
      Object.entries(active).every(([key, values]) => key === exceptKey || filterMatches(product, key, values));

    const savePreorderState = (force = false) => {
      if (shouldRestoreScroll && !force) return;
      writeCollectionState(section, {
        scrollY: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
        visibleLimit: scopedProducts.length,
        active,
        ...(paginationEnabled ? { preorderPage: currentPage } : {}),
      });
    };

    const schedulePreorderStateSave = () => {
      if (shouldRestoreScroll || saveScrollQueued) return;
      saveScrollQueued = true;
      window.requestAnimationFrame(() => {
        saveScrollQueued = false;
        savePreorderState();
      });
    };

    const revealStickyHeader = () => {
      const headerSection = document.querySelector('.section-header');
      if (!headerSection) return;
      headerSection.classList.add('shopify-section-header-sticky', 'animate');
      headerSection.classList.remove('shopify-section-header-hidden');
      document.documentElement.style.setProperty('--header-height', `${headerSection.offsetHeight}px`);
    };

    const restorePreorderScrollPosition = () => {
      if (!shouldRestoreScroll) return;
      shouldRestoreScroll = false;
      const targetScrollY = restoreScrollY;
      revealStickyHeader();
      restoreFrame = window.requestAnimationFrame(() => {
        revealStickyHeader();
        window.scrollTo(0, targetScrollY);
      });
      restoreTimers = [80, 240, 500].map((delay) => {
        return window.setTimeout(() => {
          revealStickyHeader();
          window.scrollTo(0, targetScrollY);
        }, delay);
      });
    };

    const cancelPageRestore = () => {
      shouldRestoreScroll = false;
      window.cancelAnimationFrame(restoreFrame);
      restoreTimers.forEach((timer) => window.clearTimeout(timer));
      restoreTimers = [];
    };

    const pageUrl = (page) => {
      const url = new URL(window.location.href);
      if (page > 1) url.searchParams.set('preorder_page', page);
      else url.searchParams.delete('preorder_page');
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const replacePageUrl = () => {
      window.history.replaceState({ ...window.history.state, hobbyTigerCollection: null }, '', pageUrl(currentPage));
    };

    const renderPagination = () => {
      const pages = totalPages <= 5
        ? Array.from({ length: totalPages }, (_, index) => index + 1)
        : [...new Set([1, currentPage === 1 ? 2 : currentPage === totalPages ? totalPages - 1 : currentPage, totalPages])];
      const link = (page, label, content, extraClass = '') => `<a class="ht-preorder-pagination__link ${extraClass}" href="${escapeHtml(pageUrl(page))}" data-ht-preorder-page="${page}" aria-label="${escapeHtml(label)}"${page === currentPage ? ' aria-current="page"' : ''}>${content}</a>`;
      const icon = `<span class="svg-wrapper" aria-hidden="true">${collectionText.paginationIcon || ''}</span>`;
      const previous = collectionT('paginationPrevious', 'Previous page');
      const next = collectionT('paginationNext', 'Next page');
      const arrow = (page, label, direction, enabled) => enabled
        ? link(page, label, icon, `ht-preorder-pagination__${direction}`)
        : `<span class="ht-preorder-pagination__link ht-preorder-pagination__${direction}" aria-disabled="true" aria-label="${escapeHtml(label)}">${icon}</span>`;
      const numbers = pages.map((page, index) => `${index && page - pages[index - 1] > 1 ? '<span aria-hidden="true">&hellip;</span>' : ''}${link(page, collectionT('paginationPage', 'Page [number]', { number: page }), page)}`).join('');
      pagers.forEach((nav) => {
        nav.hidden = totalPages <= 1;
        nav.innerHTML = `<span class="ht-preorder-pagination__status" role="status">${escapeHtml(collectionT('preorderPagination', 'Page [current] of [total]', { current: currentPage, total: totalPages }))}</span><div class="ht-preorder-pagination__links">${arrow(currentPage - 1, previous, 'previous', currentPage > 1)}${numbers}${arrow(currentPage + 1, next, 'next', currentPage < totalPages)}</div>`;
      });
    };

    const closeMenus = (exceptMenu) => {
      menus.forEach((menu) => {
        if (menu === exceptMenu) return;
        menu.list.hidden = true;
        menu.button.setAttribute('aria-expanded', 'false');
      });
    };

    const populateMenus = () => {
      menus.forEach((menu) => {
        const values = new Set();
        scopedProducts.forEach((product) => {
          if (!matches(product, menu.key)) return;
          fieldValues(product, menu.key).forEach((value) => values.add(value));
        });

        if (active[menu.key].length && !Array.from(values).some((value) => normalize(value) === normalize(active[menu.key][0]))) {
          active[menu.key] = [];
          menu.button.textContent = allLabel(menu.key);
        }

        const selectedValue = active[menu.key][0] || '';
        menu.list.innerHTML = `
          <button class="ht-preorder-select__option${selectedValue ? '' : ' is-selected'}" type="button" role="option" aria-selected="${selectedValue ? 'false' : 'true'}" data-ht-preorder-option data-value="">${allLabel(menu.key)}</button>
          ${Array.from(values).sort((a, b) => a.localeCompare(b)).map((value) => {
            const selected = normalize(value) === normalize(selectedValue);
            return `<button class="ht-preorder-select__option${selected ? ' is-selected' : ''}" type="button" role="option" aria-selected="${selected ? 'true' : 'false'}" data-ht-preorder-option data-value="${escapeHtml(value)}">${escapeHtml(value)}</button>`;
          }).join('')}
        `;
      });
    };

    const groupedDateTitle = (dateValue) => {
      const date = paginationEnabled && /^\d{4}-\d{2}-\d{2}$/.test(dateValue) ? parseDate(dateValue) : new Date(dateValue);
      if (!date || Number.isNaN(date.getTime())) return collectionT('dateUnavailable', 'Date unavailable');
      const day = date.getDate();
      const pageLocale = document.documentElement.lang || navigator.language || 'en-US';
      const isFrench = String(pageLocale).toLowerCase().startsWith('fr');
      const suffix = isFrench ? '' : day % 10 === 1 && day % 100 !== 11 ? 'st' : day % 10 === 2 && day % 100 !== 12 ? 'nd' : day % 10 === 3 && day % 100 !== 13 ? 'rd' : 'th';
      const month = date.toLocaleDateString(pageLocale, { month: 'long' });
      return collectionT('newAddedPreorderDate', '[month] [day], [year] New Added Pre-order', {
        month,
        day: `${day}${suffix}`,
        year: date.getFullYear(),
      });
    };

    const render = () => {
      populateMenus();
      const visible = scopedProducts.filter((product) => matches(product));
      const groups = new Map();
      visible.forEach((product) => {
        const created = Number(product.createdTs) > 0 ? Number(product.createdTs) * 1000 : Date.parse(product.createdAt);
        const key = paginationEnabled && Number.isFinite(created)
          ? new Date(created + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
          : (product.createdAt || '').slice(0, 10) || 'unknown';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(product);
      });

      let pageGroups = Array.from(groups.entries());
      if (paginationEnabled) {
        pageGroups.sort(([a], [b]) => a === 'unknown' ? 1 : b === 'unknown' ? -1 : b.localeCompare(a));
        totalPages = Math.max(1, Math.ceil(pageGroups.length / daysPerPage));
        currentPage = Math.min(currentPage, totalPages);
        if (currentPage !== pageFromUrl()) replacePageUrl();
        pageGroups = pageGroups.slice((currentPage - 1) * daysPerPage, currentPage * daysPerPage);
        renderPagination();
      }
      renderRoot.innerHTML = pageGroups.map(([dateKey, products]) => `
        <div class="ht-preorder-group">
          <h2 class="ht-preorder-group__title">${escapeHtml(groupedDateTitle(dateKey))}</h2>
          <ul class="ht-collection__grid grid product-grid contains-card contains-card--product grid--4-col-desktop grid--2-col-tablet-down" role="list">
            ${products.map((product, index) => renderProductCard(product, index, paginationEnabled ? `${section.id || 'dynamic-preorder'}-${dateKey}` : section.id || 'dynamic-preorder')).join('')}
          </ul>
        </div>
      `).join('');
      renderedPage = currentPage;

      if (paginationEnabled) {
        // Discard queued requests for cards removed by a page change; keep in-flight requests cached.
        for (let index = priceQueue.length - 1; index >= 0; index -= 1) {
          if (priceNodeForHandle(priceQueue[index].handle)) continue;
          queuedPriceHandles.delete(priceQueue[index].handle);
          priceQueue.splice(index, 1);
        }
      }

      if (empty) empty.hidden = visible.length !== 0;
      if (clear) clear.hidden = menus.every((menu) => active[menu.key].length === 0);
      section.classList.remove('ht-collection--engine-pending');
      hydratePrices(section);
      window.dispatchEvent(new CustomEvent('hobbytiger:favorites:change'));
      restorePreorderScrollPosition();
    };

    pagers.forEach((nav) => nav.addEventListener('click', (event) => {
      const link = event.target.closest('[data-ht-preorder-page]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const nextPage = pageNumber(link.dataset.htPreorderPage);
      if (nextPage === currentPage || nextPage > totalPages) return;
      savePreorderState(true);
      cancelPageRestore();
      currentPage = nextPage;
      window.history.pushState({ ...window.history.state, hobbyTigerCollection: null }, '', pageUrl(currentPage));
      render();
      revealStickyHeader();
      section.querySelector('.ht-preorder-header')?.scrollIntoView({ block: 'start', behavior: 'instant' });
      pagers[0].focus({ preventScroll: true });
      savePreorderState(true);
    }));

    if (paginationEnabled) window.addEventListener('popstate', () => {
      const saved = readCollectionState(section);
      cancelPageRestore();
      currentPage = pageNumber(saved?.preorderPage || pageFromUrl());
      menus.forEach((menu) => {
        active[menu.key] = Array.isArray(saved?.active?.[menu.key]) ? saved.active[menu.key].slice(0, 1) : [];
        menu.button.textContent = active[menu.key][0] || allLabel(menu.key);
      });
      restoreScrollY = Number(saved?.scrollY || 0);
      shouldRestoreScroll = true;
      render();
    });

    menus.forEach((menu) => {
      menu.button.addEventListener('click', () => {
        const willOpen = menu.list.hidden;
        populateMenus();
        closeMenus(menu);
        menu.list.hidden = !willOpen;
        menu.button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
      menu.list.addEventListener('click', (event) => {
        const option = event.target.closest('[data-ht-preorder-option]');
        if (!option) return;
        const value = option.dataset.value || '';
        active[menu.key] = value ? [value] : [];
        menu.button.textContent = value || allLabel(menu.key);
        closeMenus();
        clearCollectionState(section);
        shouldRestoreScroll = false;
        if (paginationEnabled) {
          cancelPageRestore();
          currentPage = 1;
          replacePageUrl();
        }
        render();
        if (paginationEnabled) savePreorderState(true);
      });
    });

    clear?.addEventListener('click', () => {
      clearCollectionState(section);
      shouldRestoreScroll = false;
      if (paginationEnabled) {
        cancelPageRestore();
        currentPage = 1;
        replacePageUrl();
      }
      menus.forEach((menu) => {
        active[menu.key] = [];
        menu.button.textContent = allLabel(menu.key);
      });
      render();
      if (paginationEnabled) savePreorderState(true);
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-ht-preorder-menu]')) closeMenus();
    });

    window.addEventListener('scroll', schedulePreorderStateSave, { passive: true });
    window.addEventListener('pagehide', () => savePreorderState(true));
    window.addEventListener('beforeunload', () => savePreorderState(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') savePreorderState(true);
    });
    renderRoot.addEventListener('click', (event) => {
      const productLink = event.target.closest('a[href*="/products/"]');
      if (productLink) {
        revealStickyHeader();
        savePreorderState(true);
      }
    });
    ['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
      renderRoot.addEventListener(eventName, (event) => {
        const productLink = event.target.closest('a[href*="/products/"]');
        if (productLink) {
          revealStickyHeader();
          savePreorderState(true);
        }
      }, { capture: true, passive: true });
    });
    window.addEventListener('pageshow', (event) => {
      if (!isBackForwardNavigation(event)) return;
      revealStickyHeader();
      const latestState = readCollectionState(section);
      if (!latestState) return;
      if (paginationEnabled) currentPage = pageNumber(latestState.preorderPage || pageFromUrl());
      restoreScrollY = Number(latestState.scrollY || 0);
      if (latestState.active && typeof latestState.active === 'object') {
        menus.forEach((menu) => {
          const values = Array.isArray(latestState.active[menu.key]) ? latestState.active[menu.key] : [];
          active[menu.key] = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).slice(0, 1);
          menu.button.textContent = active[menu.key][0] || allLabel(menu.key);
        });
      }
      if ((initialRestoreApplied || event.persisted) && renderedPage === currentPage && renderRoot.querySelector('.card-wrapper, .card')) {
        if (restoreScrollY > 0 && Math.abs((window.scrollY || 0) - restoreScrollY) > 8) {
          shouldRestoreScroll = true;
          restorePreorderScrollPosition();
        }
        return;
      }
      render();
      if (restoreScrollY > 0 && Math.abs((window.scrollY || 0) - restoreScrollY) > 8) {
        shouldRestoreScroll = true;
        restorePreorderScrollPosition();
      }
    });

    render();
    initialRestoreApplied = Boolean(restoreState);
  };

  const initCollection = (section, initialProducts, dataset = null) => {
    let scopedProducts = Array.isArray(initialProducts) ? initialProducts : [];
    const filterKeys = getFilterKeys(section);
    const countMode = section.dataset.htFilterCountMode || (section.dataset.htDisableFilterCounts === 'true' ? 'none' : 'dynamic');
    const disableFilterCounts = countMode === 'none';
    const showFilterCounts = !disableFilterCounts;
    const showStaticInitialCounts = countMode === 'static' || countMode === 'static-initial-live-after-change';
    const runInitialLiveCounts = countMode === 'dynamic';
    const runLiveCountsAfterChange = countMode === 'dynamic' || countMode === 'static-initial-live-after-change';
    const staticFacetCounts = staticFacetCountsForSection(dataset, section);
    const grid = section.querySelector('[data-ht-product-grid]');
    const empty = section.querySelector('[data-ht-empty]');
    const resultCount = section.querySelector('[data-ht-result-count]');
    const activeFilters = section.querySelector('[data-ht-active-filters]');
    const activeFilterList = section.querySelector('[data-ht-active-filter-list]');
    const clear = section.querySelector('[data-ht-clear]');
    const clearActive = section.querySelector('[data-ht-clear-active]');
    const filterPanel = section.querySelector('.ht-collection__filters');
    const filterToggle = section.querySelector('[data-ht-filter-toggle]');
    const mobileFilterOpen = section.querySelector('[data-ht-mobile-filter-open]');
    const sortSelects = Array.from(section.querySelectorAll('[data-ht-sort]'));
    const priceMin = section.querySelector('[data-ht-price-min]');
    const priceMax = section.querySelector('[data-ht-price-max]');
    const mobileFilterMedia = window.matchMedia('(max-width: 980px)');
    const filterDetails = Array.from(section.querySelectorAll('.ht-filter-group'));
    const valuesByKey = Object.fromEntries(filterKeys.map((key) => [key, new Set()]));
    const optionInputs = new Map();
    let active = {};
    let visibleLimit = batchSize;
    let currentMatches = [];
    let renderedCount = 0;
    const shouldUseSavedPosition = isBackForwardNavigation();
    const restoreState = shouldUseSavedPosition ? readCollectionState(section) : null;
    let restoreScrollY = restoreState ? Number(restoreState.scrollY || 0) : 0;
    let restoreVisibleLimit = restoreState ? Number(restoreState.visibleLimit || batchSize) : batchSize;
    let shouldRestoreScroll = restoreScrollY > 0;
    let restoreAttempts = 0;
    let usedInitialRestore = false;
    let initialRestoreApplied = false;

    if (!grid) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (!shouldUseSavedPosition) {
      clearCollectionState(section);
    }

    active = applyInitialUrlFilters(section, filterKeys, valuesByKey, scopedProducts);
    if (restoreState?.active && typeof restoreState.active === 'object') {
      filterKeys.forEach((key) => {
        const values = Array.isArray(restoreState.active[key]) ? restoreState.active[key] : [];
        active[key] = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
      });
    }
    if (restoreState?.price) {
      if (priceMin && restoreState.price.min) priceMin.value = restoreState.price.min;
      if (priceMax && restoreState.price.max) priceMax.value = restoreState.price.max;
    }
    if (restoreState?.sort) {
      sortSelects.forEach((select) => {
        select.value = restoreState.sort;
      });
    }

    let filtersHydrated = false;
    let filterHydrationQueued = false;
    let filtersIndexed = false;

    const indexFilterValues = () => {
      if (filtersIndexed) return;
      scopedProducts.forEach((product) => {
        filterKeys.forEach((key) => {
          fieldValues(product, key).forEach((value) => valuesByKey[key].add(value));
        });
      });
      filtersIndexed = true;
    };

    const hydrateFilters = () => {
      if (filtersHydrated) return;
      indexFilterValues();
      filtersHydrated = true;

      filterKeys.forEach((key) => {
        const container = section.querySelector(`[data-ht-filter-values="${key}"]`);
        const group = section.querySelector(`[data-ht-filter-group="${key}"]`);
        const values = Array.from(valuesByKey[key] || []).sort((a, b) => a.localeCompare(b));

        if (!container || !group || values.length === 0) {
          group?.setAttribute('hidden', '');
          return;
        }

        container.classList.add('is-hydrating');
        container.classList.remove('is-expanding');
        container.style.maxHeight = '0px';
        container.style.opacity = '0';
        group.hidden = false;
        container.classList.toggle('is-scrollable', values.length > 16);
        const optionsMarkup = values.map((value) => {
          const checked = active[key]?.map(normalize).includes(normalize(value));
          const inputId = `ht-filter-${key}-${slugify(value)}`;
          const initialCount = showStaticInitialCounts ? staticFacetCount(staticFacetCounts, key, value) : null;
          const countText = Number.isFinite(Number(initialCount)) ? `(${initialCount})` : '';
          const countMarkup = showFilterCounts ? ` <span class="ht-filter-option__count" data-ht-option-count>${countText}</span>` : '';
          return `
            <label class="ht-filter-option" for="${inputId}">
              <input id="${inputId}" type="checkbox" value="${escapeHtml(value)}" data-ht-filter="${key}"${checked ? ' checked' : ''}>
              <span class="ht-filter-option__text">${escapeHtml(labels[value] || value)}${countMarkup}</span>
            </label>
          `;
        }).join('');
        container.innerHTML = `
          <div class="ht-filter-group__panel-header">
            <span class="ht-filter-group__selected-count" data-ht-filter-selected-count="${key}">${escapeHtml(collectionT('selectedCount', '[count] selected', { count: 0 }))}</span>
            <button class="ht-filter-group__reset" type="button" data-ht-reset-filter="${key}">${escapeHtml(collectionT('clear', 'Clear'))}</button>
          </div>
          <div class="ht-filter-group__options">${optionsMarkup}</div>
        `;

        optionInputs.set(key, Array.from(container.querySelectorAll(`[data-ht-filter="${key}"]`)));

        const targetHeight = Math.min(container.scrollHeight, values.length > 16 ? 450 : container.scrollHeight);
        window.requestAnimationFrame(() => {
          container.classList.remove('is-hydrating');
          container.classList.add('is-expanding');
          container.style.maxHeight = `${targetHeight}px`;
          container.style.opacity = '1';
          window.setTimeout(() => {
            container.classList.remove('is-expanding');
            container.style.maxHeight = '';
            container.style.opacity = '';
          }, 320);
        });
      });
    };

    const scheduleFilterHydration = () => {
      if (filtersHydrated || filterHydrationQueued) return;
      filterHydrationQueued = true;

      const hydrateAfterCardsPaint = () => {
        const runHydration = () => {
          filterHydrationQueued = false;
          hydrateFilters();
          updateFilterPanelSummaries();
          if (!runInitialLiveCounts) return;
          const runCounts = () => updateOptionAvailability();
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(runCounts, { timeout: 800 });
          } else {
            window.setTimeout(runCounts, 0);
          }
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(runHydration, { timeout: 800 });
        } else {
          window.setTimeout(runHydration, 80);
        }
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.setTimeout(hydrateAfterCardsPaint, 0);
        });
      });
    };

    const loadStatus = document.createElement('p');
    loadStatus.className = 'ht-collection__load-status';
    loadStatus.hidden = true;
    loadStatus.textContent = collectionT('loadingMoreProducts', 'Loading more products...');
    grid.insertAdjacentElement('afterend', loadStatus);

    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-ht-load-sentinel', '');
    sentinel.style.minHeight = '1px';
    grid.insertAdjacentElement('afterend', sentinel);

    const sortValue = () => sortSelects[0]?.value || 'relevance';

    const revealStickyHeader = () => {
      const headerSection = document.querySelector('.section-header');
      if (!headerSection) return;
      headerSection.classList.add('shopify-section-header-sticky', 'animate');
      headerSection.classList.remove('shopify-section-header-hidden');
      document.documentElement.style.setProperty('--header-height', `${headerSection.offsetHeight}px`);
    };

    const saveScrollState = (force = false) => {
      if (shouldRestoreScroll && !force) return;
      writeCollectionState(section, {
        scrollY: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
        visibleLimit: Math.max(visibleLimit, renderedCount, batchSize),
        active,
        price: {
          min: priceMin?.value || '',
          max: priceMax?.value || '',
        },
        sort: sortValue(),
      });
    };

    let saveScrollQueued = false;
    const scheduleScrollStateSave = () => {
      if (shouldRestoreScroll || saveScrollQueued) return;
      saveScrollQueued = true;
      window.requestAnimationFrame(() => {
        saveScrollQueued = false;
        saveScrollState();
      });
    };

    const restoreScrollPosition = () => {
      if (!shouldRestoreScroll) return;

      const neededHeight = restoreScrollY + window.innerHeight + 160;
      const pageHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      if (pageHeight < neededHeight && visibleLimit < currentMatches.length && restoreAttempts < 40) {
        restoreAttempts += 1;
        visibleLimit = Math.min(visibleLimit + batchSize, currentMatches.length);
        renderGrid();
        return;
      }

      shouldRestoreScroll = false;
      const targetScrollY = restoreScrollY;
      revealStickyHeader();
      window.requestAnimationFrame(() => {
        revealStickyHeader();
        window.scrollTo(0, targetScrollY);
      });
      [80, 240, 500].forEach((delay) => {
        window.setTimeout(() => {
          revealStickyHeader();
          window.scrollTo(0, targetScrollY);
        }, delay);
      });
    };

    const readActive = () => {
      if (!filtersHydrated) return;
      filterKeys.forEach((key) => {
        const selected = Array.from(section.querySelectorAll(`[data-ht-filter="${key}"]:checked`)).map((input) => input.value);
        active[key] = Array.from(new Set([...(active[key] || []).filter((value) => selected.map(normalize).includes(normalize(value))), ...selected]));
      });
    };

    const matchesAll = (product, exceptKey = null) => productMatchesActive(product, active, priceMin, priceMax, exceptKey);

    const updateOptionAvailability = () => {
      if (disableFilterCounts) return;
      filterKeys.forEach((key) => {
        const inputs = optionInputs.get(key) || [];
        if (!inputs.length) return;
        const counts = new Map();
        scopedProducts.forEach((product) => {
          if (!productMatchesActive(product, active, priceMin, priceMax, key)) return;
          fieldValues(product, key).forEach((value) => {
            const countKey = normalize(value);
            counts.set(countKey, (counts.get(countKey) || 0) + 1);
          });
        });

        inputs.forEach((input) => {
          const count = counts.get(normalize(input.value)) || 0;
          const selected = input.checked || (active[key] || []).map(normalize).includes(normalize(input.value));
          const disabled = count === 0 && !selected;
          const label = input.closest('.ht-filter-option');
          const countNode = label?.querySelector('[data-ht-option-count]');
          input.disabled = disabled;
          label?.classList.toggle('is-disabled', disabled);
          if (countNode) countNode.textContent = count ? `(${count})` : '(0)';
        });
      });
    };

    let liveCountUpdateQueued = false;
    const scheduleLiveCountUpdate = () => {
      if (!runLiveCountsAfterChange || !filtersHydrated || liveCountUpdateQueued) return;
      liveCountUpdateQueued = true;

      const runCounts = () => {
        liveCountUpdateQueued = false;
        updateOptionAvailability();
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(runCounts, { timeout: 900 });
      } else {
        window.setTimeout(runCounts, 120);
      }
    };

    const updateFilterPanelSummaries = () => {
      filterKeys.forEach((key) => {
        const count = active[key]?.length || 0;
        section.querySelectorAll(`[data-ht-filter-selected-count="${key}"]`).forEach((node) => {
          node.textContent = collectionT('selectedCount', '[count] selected', { count });
        });
      });
      const priceCount = (priceMin?.value ? 1 : 0) + (priceMax?.value ? 1 : 0);
      section.querySelectorAll('[data-ht-price-selected-count]').forEach((node) => {
        node.textContent = collectionT('selectedCount', '[count] selected', { count: priceCount });
      });
    };

    const renderActiveFilters = () => {
      const chips = [];
      Object.entries(active).forEach(([key, values]) => {
        values.forEach((value) => {
          chips.push(`<button class="ht-active-filter" type="button" data-ht-remove-filter="${key}" data-ht-remove-value="${escapeHtml(value)}">${escapeHtml(labels[value] || value)}</button>`);
        });
      });
      if (priceMin?.value) chips.push(`<button class="ht-active-filter" type="button" data-ht-clear-price="min">${escapeHtml(collectionT('min', 'Min'))} ¥${escapeHtml(priceMin.value)}</button>`);
      if (priceMax?.value) chips.push(`<button class="ht-active-filter" type="button" data-ht-clear-price="max">${escapeHtml(collectionT('max', 'Max'))} ¥${escapeHtml(priceMax.value)}</button>`);
      if (activeFilterList) activeFilterList.innerHTML = chips.join('');
      if (activeFilters) activeFilters.hidden = chips.length === 0;
      if (clear) clear.hidden = chips.length === 0;
      updateFilterPanelSummaries();
    };

    const renderGrid = () => {
      const shown = Math.min(visibleLimit, currentMatches.length);
      if (renderedCount > shown) {
        grid.innerHTML = '';
        renderedCount = 0;
      }
      const productsToAppend = currentMatches.slice(renderedCount, shown);
      if (productsToAppend.length) {
        grid.insertAdjacentHTML('beforeend', productsToAppend.map((product, index) => renderProductCard(product, renderedCount + index, section.id || 'dynamic-collection')).join(''));
        renderedCount = shown;
      }
      if (resultCount) {
        const total = currentMatches.length;
        resultCount.textContent = total === 0 ? '0 of 0 products' : `1 - ${shown} of ${total} products`;
      }
      if (empty) empty.hidden = currentMatches.length !== 0;
      loadStatus.hidden = true;
      sentinel.hidden = visibleLimit >= currentMatches.length;
      section.classList.remove('ht-collection--engine-pending');
      section.classList.add('ht-collection--first-batch-ready');
      restoreScrollPosition();
      hydratePrices(section);
      window.dispatchEvent(new CustomEvent('hobbytiger:favorites:change'));
    };

    const hasMoreProducts = () => visibleLimit < currentMatches.length;
    let loadTickQueued = false;
    let lastLoadScrollY = -1;

    const loadNextBatch = () => {
      if (!hasMoreProducts() || loadTickQueued) return;
      loadTickQueued = true;
      window.requestAnimationFrame(() => {
        loadTickQueued = false;
        if (!hasMoreProducts()) return;
        visibleLimit = Math.min(visibleLimit + batchSize, currentMatches.length);
        renderGrid();
      });
    };

    const checkScrollLoad = () => {
      if (!hasMoreProducts()) return;
      const scrollBottom = window.scrollY + window.innerHeight;
      const pageBottom = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      if (pageBottom - scrollBottom > 700) return;
      const currentScrollY = Math.round(window.scrollY);
      if (currentScrollY === lastLoadScrollY) return;
      lastLoadScrollY = currentScrollY;
      loadNextBatch();
    };

    const update = (resetLimit = true, options = {}) => {
      readActive();
      if (resetLimit) {
        const applyingInitialRestore = !usedInitialRestore && Boolean(restoreState);
        visibleLimit = !usedInitialRestore ? Math.max(batchSize, restoreVisibleLimit) : batchSize;
        usedInitialRestore = true;
        if (applyingInitialRestore) initialRestoreApplied = true;
        renderedCount = 0;
        lastLoadScrollY = -1;
        grid.innerHTML = '';
      }
      currentMatches = sortProducts(scopedProducts.filter((product) => matchesAll(product)), sortValue());
      renderActiveFilters();
      renderGrid();
      if (filtersHydrated) {
        if (options.liveCounts) scheduleLiveCountUpdate();
        else if (runInitialLiveCounts) updateOptionAvailability();
      } else {
        scheduleFilterHydration();
      }
    };

    const clearAll = () => {
      clearCollectionState(section);
      shouldRestoreScroll = false;
      section.querySelectorAll('[data-ht-filter]').forEach((input) => { input.checked = false; });
      Object.fromEntries(filterKeys.map((key) => [key, []]));
      active = Object.fromEntries(filterKeys.map((key) => [key, []]));
      if (priceMin) priceMin.value = '';
      if (priceMax) priceMax.value = '';
      if (window.location.search) window.history.replaceState({}, '', window.location.pathname);
      update(true, { liveCounts: true });
    };

    const setFilterOpen = (isOpen) => {
      if (!filterPanel) return;
      filterPanel.classList.toggle('is-open', isOpen);
      filterToggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      mobileFilterOpen?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    const closeFilterDropdowns = (except = null) => {
      if (mobileFilterMedia.matches) return;
      filterDetails.forEach((details) => {
        if (details !== except) details.removeAttribute('open');
      });
    };

    section.addEventListener('change', (event) => {
      if (event.target.matches('[data-ht-filter]')) {
        clearCollectionState(section);
        shouldRestoreScroll = false;
        const key = event.target.dataset.htFilter;
        const value = event.target.value;
        if (!event.target.checked) {
          active[key] = (active[key] || []).filter((item) => normalize(item) !== normalize(value));
        }
        update(true, { liveCounts: true });
      }
      if (event.target.matches('[data-ht-sort]')) {
        clearCollectionState(section);
        shouldRestoreScroll = false;
        sortSelects.forEach((select) => {
          if (select !== event.target) select.value = event.target.value;
        });
        update();
      }
    });

    priceMin?.addEventListener('input', () => {
      clearCollectionState(section);
      shouldRestoreScroll = false;
      update(true, { liveCounts: true });
    });
    priceMax?.addEventListener('input', () => {
      clearCollectionState(section);
      shouldRestoreScroll = false;
      update(true, { liveCounts: true });
    });
    clear?.addEventListener('click', clearAll);
    clearActive?.addEventListener('click', clearAll);
    section.addEventListener('click', (event) => {
      const resetFilter = event.target.closest('[data-ht-reset-filter]');
      if (resetFilter) {
        event.preventDefault();
        clearCollectionState(section);
        shouldRestoreScroll = false;
        const key = resetFilter.dataset.htResetFilter;
        active[key] = [];
        section.querySelectorAll(`[data-ht-filter="${key}"]`).forEach((input) => { input.checked = false; });
        update(true, { liveCounts: true });
        return;
      }

      if (event.target.closest('[data-ht-reset-price]')) {
        event.preventDefault();
        clearCollectionState(section);
        shouldRestoreScroll = false;
        if (priceMin) priceMin.value = '';
        if (priceMax) priceMax.value = '';
        update(true, { liveCounts: true });
      }
    });
    filterToggle?.addEventListener('click', () => {
      if (mobileFilterMedia.matches) {
        setFilterOpen(false);
        return;
      }
      setFilterOpen(!filterPanel?.classList.contains('is-open'));
    });
    mobileFilterOpen?.addEventListener('click', () => setFilterOpen(true));
    filterDetails.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) closeFilterDropdowns(details);
      });
    });

    document.addEventListener('click', (event) => {
      if (mobileFilterMedia.matches) return;
      if (event.target.closest('.ht-collection__filters')) return;
      closeFilterDropdowns();
    });

    activeFilters?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-ht-remove-filter]');
      const clearPrice = event.target.closest('[data-ht-clear-price]');
      if (clearPrice) {
        clearCollectionState(section);
        shouldRestoreScroll = false;
        if (clearPrice.dataset.htClearPrice === 'min' && priceMin) priceMin.value = '';
        if (clearPrice.dataset.htClearPrice === 'max' && priceMax) priceMax.value = '';
        update(true, { liveCounts: true });
        return;
      }
      if (!removeButton) return;
      clearCollectionState(section);
      shouldRestoreScroll = false;
      const key = removeButton.dataset.htRemoveFilter;
      const value = removeButton.dataset.htRemoveValue;
      active[key] = (active[key] || []).filter((item) => normalize(item) !== normalize(value));
      section.querySelectorAll(`[data-ht-filter="${key}"]`).forEach((input) => {
        if (normalize(input.value) === normalize(value)) input.checked = false;
      });
      update(true, { liveCounts: true });
    });

    window.addEventListener('scroll', () => {
      checkScrollLoad();
      scheduleScrollStateSave();
    }, { passive: true });
    window.addEventListener('resize', checkScrollLoad);
    window.addEventListener('pagehide', () => saveScrollState(true));
    window.addEventListener('beforeunload', () => saveScrollState(true));
    window.addEventListener('pageshow', (event) => {
      if (!isBackForwardNavigation(event)) return;
      revealStickyHeader();
      const latestState = readCollectionState(section);
      if (latestState) {
        restoreScrollY = Number(latestState.scrollY || 0);
        restoreVisibleLimit = Number(latestState.visibleLimit || batchSize);
        if (latestState.active && typeof latestState.active === 'object') {
          filterKeys.forEach((key) => {
            const values = Array.isArray(latestState.active[key]) ? latestState.active[key] : [];
            active[key] = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
          });
        }
        if (latestState.price) {
          if (priceMin) priceMin.value = latestState.price.min || '';
          if (priceMax) priceMax.value = latestState.price.max || '';
        }
        if (latestState.sort) {
          sortSelects.forEach((select) => {
            select.value = latestState.sort;
          });
        }
        if (filtersHydrated) {
          filterKeys.forEach((key) => {
            const selected = new Set((active[key] || []).map(normalize));
            section.querySelectorAll(`[data-ht-filter="${key}"]`).forEach((input) => {
              input.checked = selected.has(normalize(input.value));
            });
          });
        }
        if ((initialRestoreApplied || event.persisted) && renderedCount > 0 && grid.children.length > 0) {
          if (restoreScrollY > 0 && Math.abs((window.scrollY || 0) - restoreScrollY) > 8) {
            shouldRestoreScroll = true;
            restoreAttempts = 0;
            restoreScrollPosition();
          }
          return;
        }
        if (restoreVisibleLimit > visibleLimit) {
          visibleLimit = Math.min(restoreVisibleLimit, currentMatches.length || restoreVisibleLimit);
        }
        renderedCount = 0;
        grid.innerHTML = '';
        currentMatches = sortProducts(scopedProducts.filter((product) => matchesAll(product)), sortValue());
        renderActiveFilters();
        renderGrid();
        if (filtersHydrated) {
          if (runLiveCountsAfterChange && Object.values(active).some((values) => values.length > 0)) scheduleLiveCountUpdate();
          else if (runInitialLiveCounts) updateOptionAvailability();
        }
      }
      if (restoreScrollY > 0 && Math.abs((window.scrollY || 0) - restoreScrollY) > 8) {
        shouldRestoreScroll = true;
        restoreAttempts = 0;
        restoreScrollPosition();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') saveScrollState(true);
    });
    grid.addEventListener('click', (event) => {
      const productLink = event.target.closest('a[href*="/products/"]');
      if (productLink) {
        revealStickyHeader();
        saveScrollState(true);
      }
    });
    ['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
      grid.addEventListener(eventName, (event) => {
        const productLink = event.target.closest('a[href*="/products/"]');
        if (productLink) {
          revealStickyHeader();
          saveScrollState(true);
        }
      }, { capture: true, passive: true });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && filterPanel?.classList.contains('is-open')) setFilterOpen(false);
      if (event.key === 'Escape') closeFilterDropdowns();
    });

    const resetFilterIndexes = () => {
      filtersIndexed = false;
      filterKeys.forEach((key) => {
        valuesByKey[key] = new Set();
      });
      if (!filtersHydrated) return;
      filtersHydrated = false;
      optionInputs.clear();
      filterKeys.forEach((key) => {
        const container = section.querySelector(`[data-ht-filter-values="${key}"]`);
        if (container) container.innerHTML = '';
      });
      hydrateFilters();
      updateFilterPanelSummaries();
    };

    const replaceProducts = (nextProducts) => {
      if (!Array.isArray(nextProducts) || nextProducts.length === 0) return;
      const currentHandles = scopedProducts.map((product) => product.handle).join('|');
      const nextHandles = nextProducts.map((product) => product.handle).join('|');
      if (currentHandles === nextHandles) return;

      scopedProducts = nextProducts;
      resetFilterIndexes();
      visibleLimit = Math.max(visibleLimit, renderedCount, batchSize);
      renderedCount = 0;
      lastLoadScrollY = -1;
      grid.innerHTML = '';
      currentMatches = sortProducts(scopedProducts.filter((product) => matchesAll(product)), sortValue());
      renderActiveFilters();
      renderGrid();
      if (runLiveCountsAfterChange && Object.values(active).some((values) => values.length > 0)) scheduleLiveCountUpdate();
    };

    if (Object.values(active).some((values) => values.length > 0) && !mobileFilterMedia.matches) setFilterOpen(true);
    update();
    return { replaceProducts };
  };

  const init = async (section) => {
    const dataset = section?.dataset?.htFilterProfile === 'bluray' ? { products: [] } : window.HobbyTigerCollectionProductData;
    if (!section || !dataset || !Array.isArray(dataset.products)) return false;

    let staticProducts = dataset.products.filter((product) => productMatchesCollection(product, section));
    let collectionDataset = dataset;
    if (section.dataset.htFilterProfile === 'bluray') {
      const liveProducts = await fetchLiveCollectionProducts(section);
      staticProducts = liveProducts.map((product) => liveProductToDatasetProduct(product, section));
      collectionDataset = { products: staticProducts };
    }
    section.classList.add('is-data-engine');
    let collectionController = null;
    if (section.dataset.htPreOrderLayout === 'true') {
      initPreorder(section, staticProducts.filter((product) => normalize(product.condition) === 'pre-order'));
    } else {
      collectionController = initCollection(section, staticProducts, collectionDataset);
    }

    if (section.dataset.htBackgroundLiveMerge === 'true') {
      mergeWithLiveProducts(section, staticProducts).then((mergedProducts) => {
        if (section.dataset.htPreOrderLayout === 'true') return;
        collectionController?.replaceProducts?.(mergedProducts);
      });
    }

    return true;
  };

  const renderStaticProductGrid = (section, grid, products, sectionId) => {
    if (!section || !grid || !Array.isArray(products)) return false;
    const handles = products.map((product) => product?.handle || '').join('|');
    if (grid.dataset.htRenderedHandles === handles && grid.children.length > 0) {
      section.classList.add('is-ready');
      hydratePrices(section);
      window.dispatchEvent(new CustomEvent('hobbytiger:favorites:change'));
      return false;
    }

    grid.innerHTML = products.map((product, index) => renderProductCard(product, index, sectionId)).join('');
    grid.dataset.htRenderedHandles = handles;
    section.classList.add('is-ready');
    hydratePrices(section);
    window.dispatchEvent(new CustomEvent('hobbytiger:favorites:change'));
    return true;
  };

  startPriceObserver();
  startPriceScrollPriority();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydratePrices(document), { once: true });
  } else {
    hydratePrices(document);
  }

  window.HobbyTigerCollectionEngine = {
    __hobbyTigerEngineSingleton: true,
    init,
    renderStaticProductGrid,
    renderProductCard,
    hydratePrices,
    sortProducts,
    productMatchesCollection,
  };
})();
